/**
 * Linha do tempo da expedição + próximos passos + registro de ocorrência
 * (Task 14). Mesma divisão de responsabilidade do `OrderStateMachine`:
 *
 * 1. **Mostrar o histórico**: cada passo registrado (`fulfillment_events`,
 *    migration 0033) com etapa de origem/destino OU ocorrência, QUEM registrou,
 *    quando, o motivo e o dado que o justificou (rastreio no despacho, tipo/nota
 *    da ocorrência). Passo espelhado do rastreio aparece como `system`.
 * 2. **Oferecer o que a máquina permite**: as etapas e os campos exigidos vêm
 *    de `nextStages`/`fulfillmentRequirementsFor` (regra pura), não de uma lista de
 *    botões mantida aqui. Etapa final não oferece avanço.
 * 3. **Registrar ocorrência**: formulário com tipo (vocabulário fechado) e nota
 *    obrigatória — a mesma validação pura (`checkOccurrence`) que o servidor usa.
 *
 * Os caminhos de gravação entram por prop (`transition`/`occurrence`): sem
 * eles a tela é leitura e DIZ isso, em vez de botões que não gravam.
 *
 * Migrado para `.tsx`/JSX real (Cluster D — Logística & Garantias, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * apenas (`createElement` → JSX): o ícone `Circle` da timeline já existia
 * antes desta migração (achado da investigação inicial do cluster) — nenhum
 * ícone novo foi adicionado. Nenhum `data-*`/aria/texto/contrato de submit
 * muda.
 */
// Import explícito de `React` — `tsx --test` (motor de `npm test`) cai no
// transform JSX clássico mesmo com `jsx: "react-jsx"` no tsconfig do app, e
// falha em runtime com "React is not defined" sem este import (ver ADR-0011).
import React, { type FormEvent, type ReactElement } from "react";
import { Circle } from "lucide-react";
import { StatusPanel } from "./StatusPanel.js";
import {
  FULFILLMENT_STAGE_LABELS,
  OCCURRENCE_KINDS,
  OCCURRENCE_KIND_LABELS,
  FULFILLMENT_STAGE_FIELD_LABELS,
  checkOccurrence,
  checkFulfillmentStageTransition,
  isOccurrenceKind,
  isTerminalStage,
  fulfillmentRequirementsFor,
  type FulfillmentStage,
  type OccurrenceKind,
  type FulfillmentStageCheck,
  type FulfillmentStageField,
  type FulfillmentStageInput
} from "../fulfillment/state-machine.js";
import { availableStageTransitions, fulfillmentTimeline } from "../fulfillment/dataset.js";
import { formatDateTime } from "../format.js";
import type { OccurrenceHandle, StageTransitionHandle } from "../fulfillment/view-props.js";
import type { FulfillmentTask } from "../api/types.js";

export interface FulfillmentStateMachineProps {
  task: FulfillmentTask;
  transition?: StageTransitionHandle;
  occurrence?: OccurrenceHandle;
}

export interface FulfillmentFormValues {
  get(name: string): unknown;
}

export type StageSubmission =
  | { ok: true; input: { to: FulfillmentStage } & FulfillmentStageInput }
  | { ok: false; check: FulfillmentStageCheck };

/**
 * Caminho de submit REAL do avanço de etapa, isolado do evento de DOM. A
 * validação usa a MESMA `checkFulfillmentStageTransition` do servidor: não substitui a
 * checagem server-side (a autoridade é o servidor), evita mandar transição já
 * inválida e diz ao operador qual campo falta.
 */
export function submitStageTransition(
  task: FulfillmentTask,
  to: FulfillmentStage,
  values: FulfillmentFormValues,
  transition: StageTransitionHandle
): StageSubmission {
  const input: FulfillmentStageInput = {};
  for (const field of fulfillmentRequirementsFor(to)) {
    const raw = values.get(field);
    if (typeof raw === "string") input[field] = raw.trim();
  }
  const check = checkFulfillmentStageTransition(task.stage, to, input);
  if (!check.ok) return { ok: false, check };
  const payload = { to, ...input };
  transition.submit(payload);
  return { ok: true, input: payload };
}

export type OccurrenceSubmission =
  | { ok: true; input: { occurrenceKind: OccurrenceKind; occurrenceNote: string } }
  | { ok: false; error: string; fields: string[] };

/** Caminho de submit REAL do registro de ocorrência, isolado do evento de DOM. */
export function submitOccurrence(
  values: FulfillmentFormValues,
  occurrence: OccurrenceHandle
): OccurrenceSubmission {
  const kindRaw = values.get("occurrenceKind");
  const noteRaw = values.get("occurrenceNote");
  const note = typeof noteRaw === "string" ? noteRaw.trim() : "";
  const check = checkOccurrence(kindRaw, note);
  if (!check.ok) return { ok: false, error: check.error, fields: check.fields };
  // `checkOccurrence` já garantiu que o tipo é conhecido.
  const occurrenceKind = kindRaw as OccurrenceKind;
  occurrence.submit({ occurrenceKind, occurrenceNote: note });
  return { ok: true, input: { occurrenceKind, occurrenceNote: note } };
}

// Redesign R6 (FEAT-002, apresentação apenas): mesmo padrão do OrderStateMachine
// — timeline em cartão tokenizado e formulários (avanço de etapa + ocorrência)
// com rótulo associado, campos tokenizados e CTA primário vermelho único.
// PRESERVA o contrato de submit (submitStageTransition/submitOccurrence), os
// data-*/aria e os vocabulários FULFILLMENT_STAGE_LABELS/OCCURRENCE_KIND_LABELS.
const FIELD_INPUT_CLASS =
  "rounded-control border border-border bg-surface px-3 py-2 text-sm text-ink " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60";
const PRIMARY_BUTTON_CLASS =
  "inline-flex w-fit items-center justify-center rounded-control bg-brand px-4 py-2 text-sm font-semibold text-on-brand " +
  "hover:bg-brand-pressed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60";

function fieldInput(to: FulfillmentStage, field: FulfillmentStageField, disabled: boolean): ReactElement {
  const id = `stage-${to}-${field}`;
  return (
    <p key={field} className="fulfillment-transition__field flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-body">
        {FULFILLMENT_STAGE_FIELD_LABELS[field]}
      </label>
      <input id={id} name={field} type="text" required disabled={disabled} data-stage-field={field} className={FIELD_INPUT_CLASS} />
    </p>
  );
}

function StageForm({
  task,
  to,
  transition
}: {
  task: FulfillmentTask;
  to: FulfillmentStage;
  transition: StageTransitionHandle;
}): ReactElement {
  const required = fulfillmentRequirementsFor(to);
  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submitStageTransition(task, to, new FormData(event.currentTarget), transition);
  }
  return (
    <form
      className="fulfillment-transition flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      data-stage-to={to}
      onSubmit={onSubmit}
    >
      <h4 className="text-base font-extrabold text-ink">{`Avançar para: ${FULFILLMENT_STAGE_LABELS[to]}`}</h4>
      {required.length ? (
        <div className="fulfillment-transition__requirements flex flex-col gap-3" data-stage-requires={required.join(",")}>
          {required.map((field) => fieldInput(to, field, transition.pending))}
        </div>
      ) : (
        <p className="fulfillment-transition__requirements text-sm text-muted" data-stage-requires="">
          Nenhum dado adicional exigido.
        </p>
      )}
      <button type="submit" disabled={transition.pending} data-action={`stage-${to}`} className={PRIMARY_BUTTON_CLASS}>
        {transition.pending ? "Registrando…" : `Registrar ${FULFILLMENT_STAGE_LABELS[to].toLowerCase()}`}
      </button>
    </form>
  );
}

function OccurrenceForm({ occurrence }: { occurrence: OccurrenceHandle }): ReactElement {
  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submitOccurrence(new FormData(event.currentTarget), occurrence);
  }
  return (
    <form
      className="fulfillment-occurrence flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      data-occurrence-form="true"
      onSubmit={onSubmit}
    >
      <h4 className="text-base font-extrabold text-ink">Registrar ocorrência</h4>
      <p className="fulfillment-occurrence__field flex flex-col gap-1">
        <label htmlFor="occurrence-kind" className="text-sm font-medium text-body">
          Tipo
        </label>
        <select
          id="occurrence-kind"
          name="occurrenceKind"
          required
          disabled={occurrence.pending}
          data-occurrence-field="occurrenceKind"
          className={FIELD_INPUT_CLASS}
        >
          {OCCURRENCE_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {OCCURRENCE_KIND_LABELS[kind]}
            </option>
          ))}
        </select>
      </p>
      <p className="fulfillment-occurrence__field flex flex-col gap-1">
        <label htmlFor="occurrence-note" className="text-sm font-medium text-body">
          Relato
        </label>
        <textarea
          id="occurrence-note"
          name="occurrenceNote"
          required
          disabled={occurrence.pending}
          data-occurrence-field="occurrenceNote"
          className={`${FIELD_INPUT_CLASS} min-h-[5rem]`}
        />
      </p>
      <button type="submit" disabled={occurrence.pending} data-action="register-occurrence" className={PRIMARY_BUTTON_CLASS}>
        {occurrence.pending ? "Registrando…" : "Registrar ocorrência"}
      </button>
    </form>
  );
}

/** Dado que justificou o passo, exibido em texto (nunca escondido). */
function payloadSummary(payload: Record<string, unknown>): string {
  const entries = Object.entries(payload).filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (!entries.length) return "sem dado adicional";
  return entries
    .map(([key, value]) => {
      if (key === "occurrenceKind" && isOccurrenceKind(value)) return `ocorrência: ${OCCURRENCE_KIND_LABELS[value]}`;
      const fieldLabel = FULFILLMENT_STAGE_FIELD_LABELS[key as FulfillmentStageField];
      return `${fieldLabel ?? key}: ${String(value)}`;
    })
    .join(" · ");
}

export function FulfillmentStateMachine({ task, transition, occurrence }: FulfillmentStateMachineProps): ReactElement {
  const timeline = fulfillmentTimeline(task);
  const transitions = availableStageTransitions(task);
  return (
    <section
      className="fulfillment-state-machine flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      data-section="fulfillment-state-machine"
    >
      <h3 className="text-lg font-extrabold text-ink">Histórico de etapas</h3>
      <p className="page__note text-sm text-muted" data-fulfillment-stage={task.stage} data-fulfillment-events={timeline.length}>
        {`Etapa atual: ${FULFILLMENT_STAGE_LABELS[task.stage]}.`}
      </p>
      {timeline.length ? (
        <ol className="fulfillment-timeline m-0 flex list-none flex-col p-0">
          {timeline.map((event, index) => (
            <li
              key={event.id}
              className="fulfillment-timeline__item relative flex gap-3 pb-4 last:pb-0"
              data-record-id={event.id}
              data-fulfillment-event-kind={event.eventKind}
              data-fulfillment-event-to={event.toStage ?? ""}
              data-fulfillment-event-actor-kind={event.actorKind}
            >
              <span className="relative flex w-4 shrink-0 flex-col items-center" aria-hidden>
                <Circle className="mt-0.5 h-3 w-3 shrink-0 fill-brand text-brand" />
                {index < timeline.length - 1 ? <span className="mt-1 w-px flex-1 bg-border" /> : null}
              </span>
              <p className="fulfillment-timeline__content m-0 flex-1 rounded-control bg-surface-soft p-3 text-sm text-body">
                <span className="fulfillment-timeline__stages">
                  {event.eventKind === "occurrence"
                    ? `Ocorrência${event.occurrenceKind ? `: ${OCCURRENCE_KIND_LABELS[event.occurrenceKind]}` : ""}`
                    : event.eventKind === "occurrence_cleared"
                      ? "Ocorrência resolvida"
                      : event.fromStage
                        ? `${FULFILLMENT_STAGE_LABELS[event.fromStage]} → ${event.toStage ? FULFILLMENT_STAGE_LABELS[event.toStage] : "?"}`
                        : `Expedição aberta em ${event.toStage ? FULFILLMENT_STAGE_LABELS[event.toStage] : "?"}`}
                </span>
                {" · "}
                <span className="fulfillment-timeline__actor">
                  {event.actorKind === "staff" ? `registrado por ${event.actor}` : `espelhado do registro de origem (${event.actor})`}
                </span>
                {" · "}
                <time dateTime={event.occurredAt}>{formatDateTime(event.occurredAt)}</time>
                {event.reason ? <span className="fulfillment-timeline__reason">{` · motivo: ${event.reason}`}</span> : null}
                <span className="fulfillment-timeline__payload">{` · ${payloadSummary(event.payload)}`}</span>
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <StatusPanel
          kind="empty"
          title="Nenhum passo registrado nesta expedição"
          detail="A ordem existe, mas ainda não tem evento na linha do tempo — nada foi inferido no lugar do histórico."
        />
      )}
      <h3 className="text-lg font-extrabold text-ink">Próximos passos</h3>
      {isTerminalStage(task.stage) ? (
        <StatusPanel
          kind="empty"
          title={`Etapa final: ${FULFILLMENT_STAGE_LABELS[task.stage]}`}
          detail="Nenhuma etapa sai desta. Tratamento de garantia/devolução abre o seu próprio caso (RMA, Task 15)."
        />
      ) : transition ? (
        <div className="fulfillment-state-machine__transitions grid grid-cols-1 gap-4 md:grid-cols-2">
          {transitions.map((option) => (
            <StageForm key={option.to} task={task} to={option.to} transition={transition} />
          ))}
        </div>
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Avanço de etapa não montado nesta visualização"
          detail={`Desta etapa a expedição pode seguir para: ${transitions
            .map((option) => FULFILLMENT_STAGE_LABELS[option.to])
            .join(", ")}. Esta montagem é só leitura.`}
        />
      )}
      {transition?.error ? (
        <StatusPanel kind="error" title="O avanço de etapa não foi registrado" detail={transition.error.message} />
      ) : null}
      {transition?.savedStage ? (
        <StatusPanel kind="empty" title={`Etapa registrada: ${FULFILLMENT_STAGE_LABELS[transition.savedStage]}`} />
      ) : null}
      <h3 className="text-lg font-extrabold text-ink">Ocorrências</h3>
      {occurrence ? (
        <OccurrenceForm occurrence={occurrence} />
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Registro de ocorrência não montado nesta visualização"
          detail="Esta montagem é só leitura: atraso, avaria, endereço, ausente e extravio se registram na tela de detalhe."
        />
      )}
      {occurrence?.error ? <StatusPanel kind="error" title="A ocorrência não foi registrada" detail={occurrence.error.message} /> : null}
      {occurrence?.savedOccurrence ? (
        <StatusPanel kind="empty" title={`Ocorrência registrada: ${OCCURRENCE_KIND_LABELS[occurrence.savedOccurrence]}`} />
      ) : null}
    </section>
  );
}
